/**
 * igcomCache — the last good IG COM dataset, kept for the next mount.
 *
 * The same lesson payrollCache.ts records, applied before it costs an evening:
 * a screen whose every mount starts at EMPTY and blocks first paint on a
 * network read makes the back button feel broken. IG COM is the sharper case
 * of the two, because BOTH its sources are slow for different reasons — the
 * live one is up to ten paged GraphQL round-trips at Shopify's pace, and the
 * stored one is the whole order history out of SharePoint.
 *
 *   FRESH (under REVALIDATE_AFTER_MS)  paint it and read NOTHING.
 *   USABLE (under MAX_AGE_MS)          paint it at once, re-read behind it,
 *                                      settle over the top. The screen says so.
 *   OLDER, or a different key          ignored; the load runs cold.
 *
 * KEYED ON WHAT WAS ASKED FOR — source and window — so the live 30-day answer
 * is never served to a screen that asked for the stored history.
 *
 * MEMORY ONLY, NEVER DISK, and here the reason is sharper than payroll's: the
 * LIVE dataset carries customers' email addresses and phone numbers, unmasked
 * for whoever is on the allowlist. Those may sit in a tab's memory for as long
 * as the tab is open; they may not be written to a disk somebody else uses.
 * (The STORED dataset has none of them by construction — see
 * IgcomOrdersService — but one cache holds both, so it takes the stricter
 * rule.)
 *
 * SYNCING CLEARS IT. A sync is the one moment the stored answer is known to
 * have changed, so serving the pre-sync copy afterwards would show the user
 * their own write not having happened.
 */
import type { IgcomOverview } from "./igcomClient";

/** Younger than this and the mount reads nothing at all. */
export const REVALIDATE_AFTER_MS = 60_000;
/** Older than this and it is not worth painting — the load runs cold. */
export const MAX_AGE_MS = 10 * 60_000;

export type IgcomSource = "stored" | "live";

interface Held { key: string; data: IgcomOverview; at: number }

let held: Held | null = null;

export function igcomCacheKey(source: IgcomSource, days: number): string {
  return `${source}|${days}`;
}

/** The last good dataset for this key, if it is worth painting. */
export function readIgcomCache(
  key: string, now = Date.now(),
): { data: IgcomOverview; age: number } | null {
  if (!held || held.key !== key) return null;
  const age = now - held.at;
  return age < MAX_AGE_MS ? { data: held.data, age } : null;
}

/** Keep a settled dataset for the next mount. */
export function writeIgcomCache(key: string, data: IgcomOverview): void {
  held = { key, data, at: Date.now() };
}

/** Forget it — after a sync, or anything that must not be answered from
 *  memory. Exported for tests as much as for callers. */
export function clearIgcomCache(): void {
  held = null;
}
